import { Injectable, inject } from '@angular/core';
import { ActivityEntityName, CodeUoM, EmbeddedCodeLike, JobItemCommandId, WorkOrder } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { lastValueFrom } from 'rxjs';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { itemLocationPatch } from '@workbuddy/ui/blocks/line.items/browser/item.browser.model';

/** One catalogue selection from the item browser cart. */
export interface JobItemAddSelection {
  name?: string;
  quantity?: number;
  codeUoM?: CodeUoM;
  location?: string;
}

/** Input to {@link JobItemAddService.add} — stage scope + selected codes. */
export interface JobItemAddRequest {
  jobId: string;
  stageId: string;
  /** When true, items are claim/pending codes (`isClaimCode`). */
  pending?: boolean;
  items: readonly JobItemAddSelection[];
}

/** Server outcome of AddItems (partial failures reported per line). */
export interface JobItemAddOutcome {
  failureCount?: number;
}

/**
 * Persists JobItem create from the catalogue browser.
 *
 * Maps browser selections to `JobItemCommandId.AddItems`, toasts success/failure, and invalidates
 * JobItem list queries. Callers (custom create dialog) own host refresh via board `params.itemsChanged`.
 */
@Injectable({ providedIn: 'root' })
export class JobItemAddService {

  private readonly entityData = inject(EntityDataServiceRaw);
  private readonly queryClient = injectQueryClient();
  private readonly toastService = inject(ToastService);
  private readonly host = injectWorkBuddyHost();

  /**
   * Adds selected catalogue lines to a stage.
   * Throws after toasting when the command rejects or transport fails; returns outcome when some lines fail.
   */
  public async add(request: JobItemAddRequest): Promise<JobItemAddOutcome> {

    const { jobId, stageId, pending } = request;
    const stage = { _id: stageId, jobId };
    const items = request.items
      .filter(item => !!item?.name)
      .map(item => ({ name: item.name, quantity: item.quantity ?? 1, codeUoM: item.codeUoM, stage, ...itemLocationPatch(item), ...(pending ? { isClaimCode: true } : {}) })) as EmbeddedCodeLike[];

    if (!jobId || !stageId || !items.length) {
      throw new Error('A job, stage and at least one item are required.');
    }

    let result: JobItemAddOutcome;
    try {
      // The contractor host owns the stage token itself: it has no WorkOrder read, and its AddItems
      // writes through the claim service rather than the concurrency-checked stage command.
      const updatedAt = this.host.isContractorPortal ? undefined : await this.loadStageToken(stageId);
      result = await lastValueFrom(this.entityData.executeCommand<JobItemAddOutcome>(ActivityEntityName.JobItem, JobItemCommandId.AddItems, { stageId, updatedAt, items }));
    } catch (error: unknown) {
      this.toastService.error(pending ? 'Add pending items' : 'Add items', this.errorMessage(error));
      throw error;
    }

    await this.queryClient.invalidateQueries({ queryKey: [ActivityEntityName.JobItem, 'items'], refetchType: 'all' });

    const failureCount = result?.failureCount ?? 0;
    const added = items.length - failureCount;

    if (failureCount) {
      this.toastService.error('Some items were not added', `${failureCount} of ${items.length} could not be added.`);
    } else {
      this.toastService.success(pending ? 'Pending items added' : 'Items added', `${added} item${added === 1 ? '' : 's'} added.`);
    }

    return result ?? {};
  }

  private async loadStageToken(stageId: string): Promise<string> {

    const stageResponse = await lastValueFrom(this.entityData.getById<WorkOrder>(ActivityEntityName.WorkOrder, stageId));
    const updatedAt = normalizeUpdatedAt(stageResponse?.item?.updatedAt);
    if (!updatedAt) throw new Error('The current stage updatedAt is required.');

    return updatedAt;
  }

  private errorMessage(error: unknown): string {

    return (error as { error?: { message?: string } })?.error?.message ?? 'The items could not be added.';
  }
}

function normalizeUpdatedAt(value: unknown): string | undefined {

  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}
